/**
 * Belegliste.
 *
 * Die Filter stehen in der Adresse, nicht nur im Zustand der Komponente:
 * „alle offenen Ausgaben aus dem März" ist eine Ansicht, die man sich
 * merken und verlinken will — von der Übersicht aus etwa mit
 * /belege?offen=true.
 */
import { useMemo, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Seite } from '../bausteine/Layout';
import { Marker } from '../bausteine/Marker';
import { Fehlermeldung } from '../bausteine/Meldung';
import { Laedt, Leer } from '../bausteine/Zustand';
import { useAbruf } from '../lib/abruf';
import { alsAbfrage } from '../lib/api';
import { datum, jahrHeute } from '../lib/format';
import { euro } from '../lib/geld';
import type { BelegListe, Kategorie } from '../lib/typen';

const PRO_SEITE = 50;

export function Belege(): ReactNode {
  const [filter, setzeFilter] = useSearchParams();

  const lese = (name: string): string => filter.get(name) ?? '';

  /** Filter ändern und dabei auf Seite 1 zurückgehen. */
  function aendere(name: string, wert: string): void {
    const neu = new URLSearchParams(filter);
    if (wert === '') neu.delete(name);
    else neu.set(name, wert);
    // Ohne diesen Rücksprung landet man auf Seite 3 einer Liste, die nach
    // dem Filtern nur noch eine Seite hat — und sieht nichts.
    neu.delete('seite');
    setzeFilter(neu, { replace: true });
  }

  const seite = Number.parseInt(lese('seite') || '1', 10);

  const pfad = useMemo(
    () =>
      `/api/belege${alsAbfrage({
        von: lese('von'),
        bis: lese('bis'),
        status: lese('status'),
        richtung: lese('richtung'),
        kategorieId: lese('kategorieId'),
        suche: lese('suche'),
        offen: lese('offen'),
        seite,
        proSeite: PRO_SEITE,
      })}`,
    // Die Zeichenkette der Suchparameter ist der Auslöser: Sie ändert sich
    // genau dann, wenn sich ein Filter geändert hat.
    [filter.toString()],
  );

  const liste = useAbruf<BelegListe>(pfad);
  const kategorien = useAbruf<Kategorie[]>('/api/kategorien');

  const seiten = liste.daten ? Math.ceil(liste.daten.gesamt / PRO_SEITE) : 1;

  return (
    <Seite
      titel="Belege"
      untertitel={liste.daten ? `${liste.daten.gesamt} erfasst` : undefined}
      aktionen={
        <Link to="/belege/neu" className="knopf haupt">
          Beleg erfassen
        </Link>
      }
    >
      <div className="karte">
        <div className="filterleiste">
          <div className="feld suchfeld">
            <label htmlFor="suche">Suche</label>
            <input
              id="suche"
              type="search"
              value={lese('suche')}
              placeholder="Bezeichnung"
              onChange={(e) => aendere('suche', e.target.value)}
            />
          </div>

          <div className="feld">
            <label htmlFor="von">Von</label>
            <input
              id="von"
              type="date"
              value={lese('von')}
              onChange={(e) => aendere('von', e.target.value)}
            />
          </div>

          <div className="feld">
            <label htmlFor="bis">Bis</label>
            <input
              id="bis"
              type="date"
              value={lese('bis')}
              onChange={(e) => aendere('bis', e.target.value)}
            />
          </div>

          <div className="feld">
            <label htmlFor="richtung">Art</label>
            <select
              id="richtung"
              value={lese('richtung')}
              onChange={(e) => aendere('richtung', e.target.value)}
            >
              <option value="">alle</option>
              <option value="einnahme">Einnahmen</option>
              <option value="ausgabe">Ausgaben</option>
            </select>
          </div>

          <div className="feld">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={lese('status')}
              onChange={(e) => aendere('status', e.target.value)}
            >
              <option value="">alle</option>
              <option value="entwurf">Entwurf</option>
              <option value="erfasst">Erfasst</option>
              <option value="gebucht">Gebucht</option>
              <option value="storniert">Storniert</option>
            </select>
          </div>

          <div className="feld">
            <label htmlFor="kategorieId">Kategorie</label>
            <select
              id="kategorieId"
              value={lese('kategorieId')}
              onChange={(e) => aendere('kategorieId', e.target.value)}
            >
              <option value="">alle</option>
              {(kategorien.daten ?? []).map((k) => (
                <option key={k.id} value={k.id}>
                  {k.bezeichnung}
                </option>
              ))}
            </select>
          </div>

          <div className="feld">
            <label htmlFor="offen">Zahlung</label>
            <select
              id="offen"
              value={lese('offen')}
              onChange={(e) => aendere('offen', e.target.value)}
            >
              <option value="">alle</option>
              <option value="true">nur unbezahlt</option>
            </select>
          </div>

          {filter.toString() !== '' ? (
            <button
              type="button"
              className="knopf schlicht klein"
              onClick={() => setzeFilter(new URLSearchParams(), { replace: true })}
            >
              Filter zurücksetzen
            </button>
          ) : null}
        </div>

        {liste.fehler ? (
          <div className="karte-koerper">
            <Fehlermeldung text={liste.fehler} />
          </div>
        ) : !liste.daten ? (
          <Laedt was="Belege werden geladen" />
        ) : liste.daten.belege.length === 0 ? (
          <Leer
            titel="Kein Beleg gefunden"
            hinweis={
              filter.toString() === ''
                ? 'Noch nichts erfasst. Der erste Beleg entsteht über „Beleg erfassen".'
                : 'Zu diesen Filtern gibt es nichts. Vielleicht ist der Zeitraum zu eng.'
            }
          />
        ) : (
          <>
            <div className="tabelle-rahmen">
              <table className="tabelle">
                <thead>
                  <tr>
                    <th className="schmal">Datum</th>
                    <th className="schmal">Nummer</th>
                    <th>Bezeichnung</th>
                    <th>Kategorie</th>
                    <th className="schmal">Zahlung</th>
                    <th className="zahl">Betrag</th>
                    <th className="schmal">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {liste.daten.belege.map((b) => (
                    <tr key={b.id}>
                      <td className="schmal">{datum(b.belegDatum)}</td>
                      <td className="schmal fest still">{b.belegnummer ?? '—'}</td>
                      <td>
                        <Link to={`/belege/${b.id}`} className="haupttext">
                          {b.bezeichnung}
                        </Link>
                        <div className="nebentext">
                          {b.kontaktName ?? ''}
                          {b.dateien > 0 ? (
                            <span title={`${b.dateien} Datei(en)`}>
                              {b.kontaktName ? ' · ' : ''}
                              {b.dateien} 📎
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        {b.kategorieBezeichnung ?? (
                          // Ohne Kategorie zählt der Beleg in keiner
                          // EÜR-Position mit — das muss auffallen.
                          <span className="marker warnung">fehlt</span>
                        )}
                      </td>
                      <td className="schmal">
                        {b.zahlungsdatum ? (
                          datum(b.zahlungsdatum)
                        ) : (
                          <span className="still klein">offen</span>
                        )}
                      </td>
                      <td className="zahl">
                        {b.richtung === 'ausgabe' ? '−' : ''}
                        {euro(b.betragBrutto, false)}
                      </td>
                      <td className="schmal">
                        <Marker status={b.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {seiten > 1 ? (
              <div className="blaetterleiste">
                <span>
                  Seite {seite} von {seiten} · {liste.daten.gesamt} Belege
                </span>
                <div className="reihe">
                  <button
                    type="button"
                    className="knopf klein"
                    disabled={seite <= 1}
                    onClick={() => aendereSeite(seite - 1)}
                  >
                    Zurück
                  </button>
                  <button
                    type="button"
                    className="knopf klein"
                    disabled={seite >= seiten}
                    onClick={() => aendereSeite(seite + 1)}
                  >
                    Weiter
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>

      <p className="klein still" style={{ marginTop: '1rem' }}>
        Für die EÜR zählt das Zahlungsdatum, nicht das Belegdatum
        (Zuflussprinzip). Ein Beleg ohne Zahlungsdatum steht als offener
        Posten und lässt sich noch nicht buchen.
      </p>
    </Seite>
  );

  function aendereSeite(neueSeite: number): void {
    const neu = new URLSearchParams(filter);
    neu.set('seite', String(neueSeite));
    setzeFilter(neu, { replace: false });
    window.scrollTo({ top: 0 });
  }
}

/** Voreinstellung für Datumsfilter: das laufende Jahr. */
export function jahresfilter(): string {
  return `von=${jahrHeute()}-01-01&bis=${jahrHeute()}-12-31`;
}
